import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { DateRangeComponentParams } from '../../services/filter-config';
import { FilterService } from '../../services/filter.service';
import { combineLatest, distinctUntilChanged, of, startWith, Subscription } from 'rxjs';
import {
	addDays,
	addMonths,
	addYears,
	getBeginjaar,
	getSchooljaarBegin,
	getSchooljaarEind,
	getSchooljaarTovHuidig,
	getSchooljarenRange,
	MAX_DATE,
	MIN_DATE,
	sameDay,
	SchooljaarHistorieFilterValue,
	SchooljaarHistorieOption,
	today,
} from '@cumlaude/shared-utils';
import {
	DatePickerDropdownComponent,
	DateRange,
	DateRangeView,
	Option,
	prettyFormat,
	SingleSelectDropdownComponent,
	SingleSelectListComponent,
} from '@cumlaude/shared-components-inputs';
import { first, isArray, isNull, last, range, sortBy } from 'lodash-es';
import { formatDate } from '@angular/common';

import { QueryParamStateService } from '../../services/query-param-state.service';
import { DashboardVariant } from '../../services/weergave-opties';
import { FilterComponent } from '../filter-component';
import { FilterName } from '../../services/filter-name';
import { NULLVALUE_LABEL } from '../../services/display.service';

export const RELATIVE_DAYS = [7, 14, 30, 60, 90];

export interface DateRangeState {
	range?: DateRange;
	view?: DateRangeView;
	relative?: number; // Als AFGELOPEN is gekozen, encodeer het aantal dagen zodat we dit dynamisch kunnen aanpassen
}

@Component({
	selector: 'app-date-range-filter',
	templateUrl: './date-range-filter.component.html',
	styleUrl: './date-range-filter.component.scss',
	imports: [SingleSelectListComponent, DatePickerDropdownComponent, SingleSelectDropdownComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeFilterComponent implements OnInit, OnDestroy, FilterComponent<DateRangeState> {
	protected readonly filterService = inject(FilterService);
	private readonly qp = inject(QueryParamStateService);

	// Velden voor FilterComponent
	filterName!: FilterName;
	inDropdown = false;
	searchInput = signal<string | undefined>(undefined);

	view = signal<DateRangeView | undefined>(undefined);
	range = signal<DateRange | undefined>(undefined);
	relative = signal<number | undefined>(undefined);

	protected viewOptions: Option<DateRangeView>[] = Object.values(DateRangeView).map((value) => new Option(value));
	protected getViewOption = (view?: DateRangeView) => this.viewOptions.find((o) => o.value === view);

	private readonly subscriptions: Subscription[] = [];

	aantalSchooljaren = signal(10);
	eindSchooljaar = signal(getSchooljaarTovHuidig());

	protected monthOptions: Signal<Option<DateRange>[]> = computed(
		() =>
			getSchooljarenRange(this.eindSchooljaar(), this.aantalSchooljaren())
				.flatMap((schooljaar) =>
					range(0, 12)
						.map((month) => addMonths(getSchooljaarBegin(schooljaar), month))
						.map((from) => ({ from, to: addDays(addMonths(from, 1), -1) }))
						.map((range) => new Option(range, formatDate(range.from, 'MMMM yyyy', 'nl-NL')))
				)
				.reverse() // aflopend gesorteerd
	);

	protected getMonthOption = (range?: DateRange) =>
		this.monthOptions().find((o) => range && o.value.from <= range.from && o.value.to >= range.from);

	protected relativeDateOptions: Option<number>[] = RELATIVE_DAYS.map((dagen) => new Option(dagen, `${dagen} dagen`));
	protected getRelativeDateOption = (dagen?: number) => this.relativeDateOptions.find(({ value }) => value === dagen);

	protected prettyFormat = prettyFormat;

	constructor() {
		effect(() => {
			const view = this.view();

			let value;
			if (view === DateRangeView.ONBEKEND) value = null;
			else value = <DateRangeState>{ view: view, range: this.range(), relative: this.relative() };

			this.filterService.setFilterInput(this.filterName, value);
		});
	}

	ngOnInit() {
		const schooljaarFilters = this.filterService.configs[this.filterName]?.componentParams;
		if (schooljaarFilters) this.subscribeSchooljaarFilters(<DateRangeComponentParams>schooljaarFilters);

		this.subscriptions.push(
			this.filterService.observeAsInput(this.filterName).subscribe((value: DateRangeState | null | undefined) => {
				if (isNull(value)) this.view.set(DateRangeView.ONBEKEND);
				else this.view.set(value?.view);

				this.range.set(value?.range);
				this.relative.set(value?.relative);
			})
		);
	}

	private subscribeSchooljaarFilters(schooljaarFilters: DateRangeComponentParams) {
		const { schooljaarActueelFilterName, schooljaarHistorieAangepastFilterName, schooljaarHistorieFilterName, schooljaarIndicatorFilterName } =
			schooljaarFilters;
		if (!schooljaarActueelFilterName) return;

		this.subscriptions.push(
			combineLatest([
				this.qp.observe('variant'),
				this.qp.observe('indicator-over'),
				...[
					schooljaarActueelFilterName,
					schooljaarHistorieFilterName,
					schooljaarHistorieAangepastFilterName,
					schooljaarIndicatorFilterName,
				].map((filterName) =>
					filterName ? this.filterService.observeAsInput(filterName).pipe(startWith(undefined), distinctUntilChanged()) : of(undefined)
				),
			]).subscribe(([variant, indicatorOver, schooljaarActueel, schooljaarHistorie, schooljaarHistorieAangepast, schooljaarIndicator]) =>
				this.updateSchooljaren(
					variant,
					indicatorOver,
					schooljaarActueel,
					schooljaarHistorie,
					schooljaarHistorieAangepast,
					schooljaarIndicator
				)
			)
		);
	}

	ngOnDestroy() {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	private updateSchooljaren(
		variant: DashboardVariant,
		indicatorOver: number | undefined,
		schooljaarActueel: string | undefined,
		schooljaarHistorie: SchooljaarHistorieFilterValue | undefined,
		schooljaarHistorieAangepast: string[] | undefined,
		schooljaarIndicator: string | undefined
	) {
		if (schooljaarIndicator && indicatorOver) {
			this.eindSchooljaar.set(schooljaarIndicator);
			this.aantalSchooljaren.set(Math.ceil(indicatorOver));
		} else if (
			variant === DashboardVariant.HISTORIE &&
			schooljaarHistorie?.option === SchooljaarHistorieOption.AANGEPAST &&
			isArray(schooljaarHistorieAangepast) &&
			schooljaarHistorieAangepast.length
		) {
			const sorted = sortBy(schooljaarHistorieAangepast);
			const eerste = first(sorted)!;
			const laatste = last(sorted)!;
			this.eindSchooljaar.set(laatste);
			this.aantalSchooljaren.set(getBeginjaar(laatste) - getBeginjaar(eerste));
		} else if (variant === DashboardVariant.HISTORIE && schooljaarHistorie) {
			this.eindSchooljaar.set(getSchooljaarTovHuidig(schooljaarHistorie.inclHuidig ? 0 : -1));
			const matches = schooljaarHistorie.option.match(/Afgelopen (\d+) schooljaren/);
			if (matches) this.aantalSchooljaren.set(Number(matches[1]));
		} else if (schooljaarActueel) {
			this.eindSchooljaar.set(schooljaarActueel);
			this.aantalSchooljaren.set(1);
		}

		// Als de huidige range buiten het schooljaar valt, maak dan de selectie leeg
		const range = this.range();
		const eind = getSchooljaarEind(this.eindSchooljaar());
		if (range && (range.from > eind || range.to <= addYears(eind, -this.aantalSchooljaren() - 1))) this.range.set(undefined);
	}

	protected setView(view: DateRangeView) {
		const convert = (range: DateRange) => {
			// Voorkom dat 1900-01-01 als datum wordt gekozen als je switcht van TOT_EN_MET naar een andere view
			const date = sameDay(range.from, MIN_DATE) ? range.to : range.from;
			switch (view) {
				case DateRangeView.MAAND:
					return this.getMonthOption({ from: date, to: range.to })?.value;
				case DateRangeView.WEEK:
					return {
						from: addDays(date, -(date.getDay() + 6) % 7),
						to: addDays(date, 6 - ((date.getDay() + 6) % 7)),
					};
				case DateRangeView.DAG:
					return { from: date, to: date };
				case DateRangeView.VANAF:
					return { from: date, to: MAX_DATE };
				case DateRangeView.TOT_EN_MET:
					return { from: MIN_DATE, to: date };
				default:
					return range;
			}
		};

		this.view.set(view);
		if (view === DateRangeView.AFGELOPEN) {
			const range = this.range();
			const option =
				this.relativeDateOptions.find(({ value }) => range && range.from >= addDays(today(), -value)) ?? this.relativeDateOptions[0];
			this.setRelative(option.value);
		} else {
			this.range.update((current) => (current ? convert(current) : convert(defaultDateRange())));
			this.relative.set(undefined);
		}
	}

	protected setRelative(dagen: number) {
		this.relative.set(dagen);
		this.range.set(getRelativeDateRange(dagen));
	}

	protected readonly DateRangeView = DateRangeView;
}

function defaultDateRange() {
	const from = today();
	from.setDate(1);
	const to = addDays(addMonths(from, 1), -1);
	return { from, to };
}

export function formatDateRangeState(state: DateRangeState | null | undefined): string | undefined {
	if(isNull(state)) return NULLVALUE_LABEL;
	if (!state?.view || !state?.range?.from || !state?.range?.to) return undefined;

	switch (state.view) {
		case DateRangeView.MAAND:
			return formatDate(state.range.from, 'MMMM yyyy', 'nl-NL');
		case DateRangeView.AFGELOPEN:
			return `Afgelopen ${state.relative} dagen`;
		case DateRangeView.VANAF:
			return `Vanaf ${formatDate(state.range.from, 'd MMM yyyy', 'nl-NL')}`;
		case DateRangeView.TOT_EN_MET:
			return `T/m ${formatDate(state.range.to, 'd MMM yyyy', 'nl-NL')}`;
		default:
			return prettyFormat(state.range, state.view);
	}
}

export function getRelativeDateRange(dagen: number): DateRange {
	return { from: addDays(today(), -dagen), to: today() };
}
